import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/types/database";

const url = import.meta.env.VITE_SUPABASE_URL;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anon) {
  throw new Error("Missing VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY");
}

export const supabase = createClient<Database>(url, anon, {
  auth: { persistSession: true, autoRefreshToken: true, storageKey: "hp-admin-auth" },
});

export const FN_BASE = `${url}/functions/v1`;

// Gedeeld "medewerker"-account achter de 4-cijferige code. Een sessie op dit e-mailadres
// = medewerker-modus (afgeslankte weergave zonder financiën).
export const STAFF_EMAIL = "medewerker@odeaandeharing.nl";

export function isStaffSession(session: { user?: { email?: string | null } | null } | null | undefined): boolean {
  return (session?.user?.email ?? "").toLowerCase() === STAFF_EMAIL;
}

/** Log in als deur-helper met een 4-cijferige code → sessie op het gedeelde account. */
export async function staffLogin(code: string): Promise<void> {
  const res = await fetch(`${FN_BASE}/staff-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: anon },
    body: JSON.stringify({ code: code.trim() }),
  });
  if (!res.ok) {
    let msg = "Inloggen mislukt";
    try { msg = (await res.json()).error ?? msg; } catch { /* ignore */ }
    throw new Error(msg);
  }
  const { access_token, refresh_token } = await res.json() as { access_token: string; refresh_token: string };
  const { error } = await supabase.auth.setSession({ access_token, refresh_token });
  if (error) throw error;
}

export async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = new Headers(init.headers);
  if (session?.access_token) headers.set("Authorization", `Bearer ${session.access_token}`);
  if (!headers.has("apikey")) headers.set("apikey", anon);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  return fetch(`${FN_BASE}${path}`, { ...init, headers });
}
